import { cycleEnded, isActiveRecommendation } from './allotment';
import type {
  HistoryTrustee,
  RecommendationHistoryRow,
  StaffRecommendationHistoryRow,
  TrusteeRecommendationSummary,
} from './dto/recommendation-history';
import type { ReferralStatus } from './enums';

export const HISTORY_FILTERS = ['all', 'active', 'expired'] as const;
export type HistoryFilter = (typeof HISTORY_FILTERS)[number];
export const HISTORY_FILTER_LABELS: Record<HistoryFilter, string> = {
  all: 'All',
  active: 'Active',
  expired: 'Expired',
};

export function recommendationState(
  r: { status: ReferralStatus; cycleEndsOn: string | null | undefined },
  now: Date = new Date(),
): Pick<RecommendationHistoryRow, 'active' | 'expired'> {
  return {
    active: isActiveRecommendation({ countsTowardAllotment: true, ...r }, now),
    expired: cycleEnded(r.cycleEndsOn, now),
  };
}

export const trusteeName = (t: HistoryTrustee) => t.displayName.trim() || t.email;

export function summarizeRecommendationHistory(
  rows: readonly StaffRecommendationHistoryRow[],
): TrusteeRecommendationSummary[] {
  const byTrustee = new Map<string, TrusteeRecommendationSummary>();
  for (const row of rows) {
    let entry = byTrustee.get(row.trustee.id);
    if (!entry) {
      entry = { trustee: row.trustee, total: 0, active: 0, expired: 0, lastUsedAt: null };
      byTrustee.set(row.trustee.id, entry);
    }
    entry.total++;
    if (row.active) entry.active++;
    if (row.expired) entry.expired++;
    if (row.usedAt && (!entry.lastUsedAt || Date.parse(row.usedAt) > Date.parse(entry.lastUsedAt)))
      entry.lastUsedAt = row.usedAt;
  }
  return [...byTrustee.values()].sort(
    (a, b) =>
      b.active - a.active ||
      trusteeName(a.trustee).localeCompare(trusteeName(b.trustee), undefined, { sensitivity: 'base' }) ||
      a.trustee.email.localeCompare(b.trustee.email),
  );
}

export function filterRecommendationHistory<
  T extends RecommendationHistoryRow & { trustee?: HistoryTrustee },
>(rows: readonly T[], { show, q }: { show: HistoryFilter; q: string }): T[] {
  const term = q.trim().toLowerCase();
  return rows.filter((r) => {
    if (show === 'active' && !r.active) return false;
    if (show === 'expired' && !r.expired) return false;
    if (!term) return true;
    return [
      r.familyLastName,
      r.referenceNumber,
      r.cycle?.label,
      r.cycle?.code,
      r.trustee?.displayName,
      r.trustee?.email,
      ...r.childNames,
    ].some((v) => v?.toLowerCase().includes(term));
  });
}
